// WorkspacePickerDialog — unified picker som samlar New / Recent /
// Cloud-flödena under ett tabs-baserat UI (#9, POST_QA_SUBPLAN
// Wave E.2 step 4).
//
// Tre tabs:
//   - "new"    → NewWorkflowSection (type-chooser, rensar canvas)
//   - "recent" → LoadSection (localStorage-picker)
//   - "cloud"  → CloudWorkspaceSection (krypterad cloud-storage)
//
// `defaultTab`-prop tillåter caller att deep-länka till en specifik
// flik (t.ex. "Skapa nytt"-knapp öppnar med defaultTab='new'). Tabs
// kan bytas mid-session.
//
// **De ursprungliga fasaderna (NewWorkflowDialog / LoadDialog /
//   CloudWorkspaceDialog) finns kvar och fortsätter funka.** Denna
// dialog är ett alternativt entry-point. Migrering av call-sites
// sker stegvis i framtida PR:er.

import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sparkles, FolderOpen, Cloud, Lock } from 'lucide-react';
import { usePlan } from '@/hooks/usePlan';
import type { Document } from '@/types/document';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';
import { NewWorkflowSection, type WorkflowKind } from './NewWorkflowSection';
import { LoadSection } from './LoadSection';
import { CloudWorkspaceSection } from './CloudWorkspaceSection';

export type WorkspacePickerTab = 'new' | 'recent' | 'cloud';

interface WorkspacePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: WorkspacePickerTab;
  /** Dölj New-tab när host renderar sin egen `NewWorkflowDialog`
   *  (t.ex. via en separat trigger som inte är coupled till
   *  Load/Cloud). Default: true (visa alla 3 tabs). */
  showNewTab?: boolean;

  // -- New-tab deps (krävs bara när showNewTab=true) ------------------
  /** Triggar canvas-rens + setCurrentDocumentId(null). */
  onNew?: (kind: WorkflowKind) => void;
  /** Sant om user är på Enterprise-tier. */
  canRunProcesses?: boolean;
  /** Sant om user:s plan tillåter co-op (Advanced+). */
  canUseCoop?: boolean;

  // -- Recent-tab deps ------------------------------------------------
  onList: () => Promise<Document[]>;
  onLoadLocal: (id: string, title: string) => void;
  onDelete: (id: string) => Promise<void>;
  onOpenRepoUpload?: () => void;

  // -- Cloud-tab deps -------------------------------------------------
  currentState: () => WorkflowState | MultiSheetWorkflowState;
  currentName: string;
  onLoadCloud: (state: WorkflowState | MultiSheetWorkflowState, name: string, docId: string) => void;
}

export function WorkspacePickerDialog({
  open, onOpenChange, defaultTab = 'recent', showNewTab = true,
  onNew, canRunProcesses, canUseCoop,
  onList, onLoadLocal, onDelete, onOpenRepoUpload,
  currentState, currentName, onLoadCloud,
}: WorkspacePickerDialogProps) {
  const { limits } = usePlan();
  // Förhindra "new"-default när showNewTab=false.
  const effectiveDefault: WorkspacePickerTab = !showNewTab && defaultTab === 'new'
    ? 'recent'
    : defaultTab;
  const [tab, setTab] = useState<WorkspacePickerTab>(effectiveDefault);
  const [cloudDocsCount, setCloudDocsCount] = useState(0);

  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg h-[80vh] flex flex-col p-0 gap-0"
        data-testid="workspace-picker-dialog"
      >
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle>Arbetsytor</DialogTitle>
          <DialogDescription className="text-xs">
            Skapa nytt, öppna ett sparat lokalt flöde, eller öppna ett krypterat
            cloud-flöde.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as WorkspacePickerTab)}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="px-6 pt-4 flex-shrink-0">
            <TabsList className={showNewTab ? 'grid grid-cols-3 w-full' : 'grid grid-cols-2 w-full'}>
              {showNewTab && (
                <TabsTrigger value="new" data-testid="workspace-picker-tab-new">
                  <Sparkles className="w-4 h-4 mr-1.5" /> Nytt
                </TabsTrigger>
              )}
              <TabsTrigger value="recent" data-testid="workspace-picker-tab-recent">
                <FolderOpen className="w-4 h-4 mr-1.5" /> Senaste
              </TabsTrigger>
              <TabsTrigger value="cloud" data-testid="workspace-picker-tab-cloud">
                <Cloud className="w-4 h-4 mr-1.5" /> Cloud
              </TabsTrigger>
            </TabsList>
          </div>

          {/* NEW */}
          {showNewTab && onNew && (
            <TabsContent value="new" className="px-6 pb-6 mt-4 flex-1 overflow-y-auto data-[state=inactive]:hidden">
              <NewWorkflowSection
                onConfirm={onNew}
                onCancel={handleClose}
                canRunProcesses={canRunProcesses ?? false}
                canUseCoop={canUseCoop ?? false}
              />
            </TabsContent>
          )}

          {/* RECENT */}
          <TabsContent value="recent" className="px-6 pb-6 mt-4 flex-1 flex flex-col min-h-0 data-[state=inactive]:hidden">
            <LoadSection
              open={open && tab === 'recent'}
              onClose={handleClose}
              onList={onList}
              onLoad={onLoadLocal}
              onDelete={onDelete}
              onOpenRepoUpload={onOpenRepoUpload}
            />
          </TabsContent>

          {/* CLOUD */}
          <TabsContent value="cloud" className="mt-4 flex-1 flex flex-col min-h-0 data-[state=inactive]:hidden">
            <div className="px-6 pb-2 text-xs text-muted-foreground flex items-center gap-1.5">
              <Lock className="w-3 h-3" />
              Krypterad lagring — {cloudDocsCount}/{limits.maxCloudStorage === 0 ? '0' : limits.maxCloudStorage} använda
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-muted font-medium">
                {limits.name}
              </span>
            </div>
            <CloudWorkspaceSection
              open={open && tab === 'cloud'}
              onClose={handleClose}
              currentState={currentState}
              currentName={currentName}
              onLoad={onLoadCloud}
              onDocsCountChange={setCloudDocsCount}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
